import type {
  AgentCard,
  ClientAction,
  DomainKnowledgeReference,
  RejectedActionReason,
  ReqGuardAction,
  ReqGuardCard,
  WizardSnapshot,
} from '@cypremos/shared'
import type { CoverageRunSummary, GenerationEvidence, RequirementCheckEvidence, ReviewRunSummary } from './chatActionBridgeContext'

export type ChainStepStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped'

export type ConfirmGate = 'testCases' | 'robot'

/** Rich content attached to an assistant message by the agent chat. */
export type ChatBlock =
  | { kind: 'action'; action: ClientAction; status: 'running' | 'done' | 'failed'; detail?: string }
  | { kind: 'rejected'; name: string; reason: RejectedActionReason }
  /**
   * Human gate: only a click on this card (or the wizard's own button) moves on.
   * 'testCases' follows classification review (bound to one classification revision);
   * 'robot' follows test-case review.
   */
  | {
      kind: 'confirm'
      gate: ConfirmGate
      revision: number
      status: 'pending' | 'running' | 'confirmed' | 'expired'
      /** Robot gate: selected cases that state assumptions — pressing the card accepts them. */
      assumptionCount?: number
    }
  | { kind: 'chain'; steps: { key: 'testCases' | 'robot'; status: ChainStepStatus }[]; detail?: string; error?: string }
  | { kind: 'card'; card: AgentCard }
  /** The reply drew on a domain knowledge base (e.g. the headlamp-control draft). Collapsed. */
  | { kind: 'knowledge'; reference: DomainKnowledgeReference }
  /** Evidence under a classification: open points and matched knowledge per requirement. Collapsed. */
  | { kind: 'evidence'; evidence: RequirementCheckEvidence; revision: number }
  /** Evidence under generated test cases: their 「仮定」 and cited knowledge. Collapsed. */
  | { kind: 'generationEvidence'; evidence: GenerationEvidence }
  /** Requirement Doctor: a page action the Chat ran; `summary` when it produced a coverage round. */
  | {
      kind: 'rgAction'
      action: ReqGuardAction
      status: 'running' | 'done' | 'failed'
      detail?: string
      summary?: CoverageRunSummary
      /** A finished 要求品質レビュー analysis. */
      reviewSummary?: ReviewRunSummary
    }
  /**
   * Requirement Doctor human gate: only a click applies the accepted proposals and re-reviews.
   * Bound to one coverage round; `summary` is the new round once applied.
   */
  | { kind: 'rgConfirm'; roundIndex: number; acceptedCount: number; status: 'pending' | 'running' | 'confirmed' | 'expired' | 'failed'; summary?: CoverageRunSummary; error?: string }
  | { kind: 'rgCard'; card: ReqGuardCard }

/** Selected cases that state assumptions, for the Robot confirm card. */
export function selectedAssumptionCount(snapshot: WizardSnapshot): number {
  return snapshot.testCases.filter((tc) => snapshot.selectedTestCaseIds.includes(tc.id) && (tc.assumptionCount ?? 0) > 0).length
}
